import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "section" | "li" | "article";
  once?: boolean;
};

// Detect reduced-motion preference once per session
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ---- Shared IntersectionObserver (one per threshold/rootMargin combo) ----
type Cb = (visible: boolean) => void;
const subscribers = new WeakMap<Element, Cb>();
let sharedObserver: IntersectionObserver | null = null;
let pending: Array<[Element, boolean]> = [];
let rafId = 0;

function flush() {
  rafId = 0;
  const batch = pending;
  pending = [];
  for (const [el, vis] of batch) {
    const cb = subscribers.get(el);
    if (cb) cb(vis);
  }
}

function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null;
  if (sharedObserver) return sharedObserver;
  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const e of entries) pending.push([e.target, e.isIntersecting]);
      // Coalesce DOM writes into one frame
      if (!rafId) rafId = requestAnimationFrame(flush);
    },
    { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
  );
  return sharedObserver;
}

function observe(el: Element, cb: Cb) {
  const io = getObserver();
  if (!io) return () => {};
  subscribers.set(el, cb);
  io.observe(el);
  return () => {
    io.unobserve(el);
    subscribers.delete(el);
  };
}

export function Reveal({ children, className, delay = 0, as: Tag = "div", once = true }: Props) {
  const ref = useRef<HTMLElement | null>(null);
  // If reduced motion: render visible immediately, skip observer + transitions.
  const reduced = useRef<boolean>(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    reduced.current = prefersReducedMotion();
    if (reduced.current) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    let done = false;
    const stop = observe(el, (vis) => {
      if (vis) {
        setVisible(true);
        if (once && !done) {
          done = true;
          stop();
        }
      } else if (!once) {
        setVisible(false);
      }
    });
    return stop;
  }, [once]);

  const animate = !reduced.current;

  return (
    <Tag
      ref={ref as never}
      style={animate ? { transitionDelay: visible ? `${delay}ms` : "0ms" } : undefined}
      className={cn(
        animate && "transition-all duration-700 ease-out will-change-[opacity,transform] motion-reduce:transition-none",
        animate && (visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"),
        className,
      )}
    >
      {children}
    </Tag>
  );
}
